import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
    FaSearch, FaMapMarkerAlt, FaBriefcase, FaLock,
    FaArrowRight, FaRegCalendarAlt, FaBuilding, FaCheckCircle,
    FaTimesCircle, FaCalendarCheck, FaHourglassHalf, FaBan, FaEye,
    FaClock, FaCheck, FaShieldAlt, FaBriefcase as FaJob
} from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading, showConfirm } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import { Link } from 'react-router-dom';
import './StudentJobPortal.css';
import PublicJobDetail from '../../PublicPortal/PublicJobDetail';

/* Colour palette for company logos – deterministic based on first char */
const LOGO_COLORS = [
    ['#1E3A8A', '#3B82F6'],   // A, K, U
    ['#065F46', '#10B981'],   // B, L, V
    ['#7C2D12', '#F97316'],   // C, M, W
    ['#4C1D95', '#8B5CF6'],   // D, N, X
    ['#831843', '#EC4899'],   // E, O, Y
    ['#1E3A8A', '#6366F1'],   // F, P, Z
    ['#134E4A', '#14B8A6'],   // G, Q, 0-3
    ['#713F12', '#EAB308'],   // H, R, 4-6
    ['#0C4A6E', '#0EA5E9'],   // I, S, 7-9
    ['#3F1F1F', '#EF4444'],   // J, T
];

const getLogoColor = (name = '') => {
    const c = name.charCodeAt(0) || 65;
    return LOGO_COLORS[c % LOGO_COLORS.length];
};

const STATUS_CONFIG = {
    Pending: { label: 'Applied', color: '#92400E', bg: '#FEF3C7', icon: <FaHourglassHalf /> },
    Shortlisted: { label: 'Shortlisted ✓', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Rejected: { label: 'Not Selected', color: '#B91C1C', bg: '#FEE2E2', icon: <FaTimesCircle /> },
    'Interview Scheduled': { label: 'Interview Scheduled', color: '#1D4ED8', bg: '#DBEAFE', icon: <FaCalendarCheck /> },
    'Interview Completed': { label: 'Interview Completed', color: '#065F46', bg: '#D1FAE5', icon: <FaCheckCircle /> },
    Withdrawn: { label: 'Withdrawn', color: '#475569', bg: '#F1F5F9', icon: <FaBan /> }
};

const StudentJobPortal = () => {
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState({});   // jobId → status
    const [loading, setLoading] = useState(true);
    const [isLocked, setIsLocked] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [selectedJob, setSelectedJob] = useState(null);

    const fetchJobs = async () => {
        try {
            const token = localStorage.getItem('token');
            const [jobsRes, appsRes] = await Promise.all([
                axios.get('/api/student/jobs', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/student/applications', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setJobs(jobsRes.data);

            const appMap = {};
            appsRes.data.forEach(app => {
                const id = app.jobId?._id || app.jobId;
                if (id) appMap[id] = app.status;
            });
            setApplications(appMap);
            setLoading(false);
        } catch (err) {
            if (err.response?.status === 403) setIsLocked(true);
            else showError('Error', 'Failed to fetch job postings');
            setLoading(false);
        }
    };

    useEffect(() => { fetchJobs(); }, []);

    const handleApply = async (job) => {
        const confirmResult = await showConfirm(
            'Apply for Position',
            `Confirm application for "${job.title}" at ${job.company}? Your verified resume will be shared with the recruiter.`
        );
        if (!confirmResult.isConfirmed) return;

        showLoading('Submitting Application…');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/student/jobs/${job._id}/apply`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Submitted!', 'Your application has been received. You will be notified of any updates.');
            setApplications(prev => ({ ...prev, [job._id]: 'Pending' }));
            setSelectedJob(null);
        } catch (err) {
            closeLoading();
            showError('Application Failed', err.response?.data?.message || 'Could not submit your application.');
        }
    };

    const filteredJobs = jobs.filter(j =>
        (j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            j.company?.toLowerCase().includes(searchTerm.toLowerCase())) &&
        j.location?.toLowerCase().includes(locationFilter.toLowerCase())
    );

    if (loading) return <Loader />;

    if (isLocked) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
                {/* Decorative background blobs */}
                {/* <div className="jp-locked-blob jp-blob-1" />
                <div className="jp-locked-blob jp-blob-2" /> */}

                <div className="jp-locked-card">
                    {/* Lock icon with pulse ring */}
                    <div className="jp-lock-icon-wrap">
                        <div className="jp-lock-pulse" />
                        <div className="jp-lock-icon-inner">
                            <FaLock />
                        </div>
                    </div>

                    {/* Badge */}
                    <span className="jp-locked-badge"><FaShieldAlt style={{ fontSize: '11px' }} /> Restricted Access</span>

                    {/* Heading */}
                    <h1 className="jp-locked-title">Career Portal Locked</h1>
                    <p className="jp-locked-desc">
                        The Job Portal is an exclusive benefit for enrolled students.<br />
                        Complete your first course enrollment to unlock curated career opportunities from our enterprise partners.
                    </p>

                    {/* Steps */}
                    <div className="jp-locked-steps">
                        <div className="jp-step jp-step-done">
                            <div className="jp-step-circle"><FaCheck /></div>
                            <div className="jp-step-text">
                                <strong>Create Account</strong>
                                <span>Already completed</span>
                            </div>
                        </div>
                        <div className="jp-step-connector" />
                        <div className="jp-step jp-step-active">
                            <div className="jp-step-circle">2</div>
                            <div className="jp-step-text">
                                <strong>Enroll in a Course</strong>
                                <span>Required to unlock portal</span>
                            </div>
                        </div>
                        <div className="jp-step-connector" />
                        <div className="jp-step jp-step-locked">
                            <div className="jp-step-circle"><FaLock style={{ fontSize: '12px' }} /></div>
                            <div className="jp-step-text">
                                <strong>Access Job Portal</strong>
                                <span>Unlocks after enrollment</span>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <Link to="/student-dashboard/catalog" className="jp-locked-cta">
                        Browse Courses &amp; Unlock <FaArrowRight />
                    </Link>

                    <p className="jp-locked-note">
                        Hundreds of verified job listings waiting for you
                    </p>
                </div>
            </div>
        );
    }


    return (
        <div className="job-portal fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Career Gateway</h1>
                <p className="page-subtitle">Verified opportunities from our enterprise partners, tailored to your learning path.</p>
            </div>

            {/* Stats strip */}
            <div className="sjp-stats-strip">
                <div className="sjp-stat">
                    <span>{jobs.length}</span>
                    <p>Open Roles</p>
                </div>
                <div className="sjp-stat">
                    <span>{Object.keys(applications).length}</span>
                    <p>Applied</p>
                </div>
                <div className="sjp-stat sjp-stat-highlight">
                    <span>{Object.values(applications).filter(s => s === 'Interview Scheduled').length}</span>
                    <p>Interviews</p>
                </div>
            </div>

            {/* Search */}
            <div className="job-search-strip">
                <div style={{ flex: 1, position: 'relative' }}>
                    <FaSearch className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Search by title, company or skill…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div style={{ flex: 0.5, position: 'relative' }}>
                    <FaMapMarkerAlt className="sjp-search-icon" />
                    <input
                        className="glass-input sjp-search-input"
                        placeholder="Location…"
                        value={locationFilter}
                        onChange={e => setLocationFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* Job list */}
            <div className="job-list">
                {filteredJobs.length > 0 ? filteredJobs.map(job => {
                    const appStatus = applications[job._id];
                    const statusCfg = STATUS_CONFIG[appStatus];
                    const [from, to] = getLogoColor(job.company);
                    const salary = job.salaryRange || job.salary || null;

                    return (
                        <div
                            key={job._id}
                            className={`job-card ${appStatus ? 'sjp-applied' : ''}`}
                            onClick={() => setSelectedJob(job)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="job-main-info">
                                {/* Coloured company logo */}
                                <div
                                    className="company-logo"
                                    style={{ background: job.companyId?.logoUrl ? 'white' : `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
                                >
                                    {job.companyId?.logoUrl ? (
                                        <img
                                            src={`${API_URL}${job.companyId.logoUrl}`}
                                            alt={job.company}
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }}
                                        />
                                    ) : (
                                        <span className="company-logo-letter">{job.company?.charAt(0)?.toUpperCase()}</span>
                                    )}
                                </div>

                                <div className="job-details">
                                    <h3 className="job-title-text">{job.title}</h3>
                                    <div className="company-name">{job.company}</div>
                                    <div className="job-tags">
                                        <span className="job-tag job-tag-location">
                                            <FaMapMarkerAlt /> {job.location}
                                        </span>
                                        {job.experience && (
                                            <span className="job-tag job-tag-exp">
                                                <FaBriefcase /> {job.experience}
                                            </span>
                                        )}
                                        <span className="job-tag job-tag-date">
                                            <FaRegCalendarAlt /> {new Date(job.postedAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                        {job.type && (
                                            <span className="job-tag job-tag-type">
                                                <FaClock /> {job.type}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="job-action-area" onClick={e => e.stopPropagation()}>
                                {salary ? (
                                    <div className="salary-text">
                                        Pay: {salary}
                                    </div>
                                ) : (
                                    <div className="salary-text salary-negotiable">Open to Negotiate</div>
                                )}

                                {appStatus ? (
                                    <div className="sjp-status-area">
                                        <span className="sjp-status-badge" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                                            {statusCfg.icon} {statusCfg.label}
                                        </span>
                                        <button className="sjp-view-btn" onClick={(e) => { e.stopPropagation(); setSelectedJob(job); }}>
                                            <FaEye /> View Details
                                        </button>
                                    </div>
                                ) : (
                                    <button className="apply-btn" onClick={(e) => { e.stopPropagation(); handleApply(job); }}>
                                        Apply Now <FaArrowRight />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                }) : (
                    <div className="sjp-empty-state">
                        <FaBuilding size={48} />
                        <h3>No opportunities found</h3>
                        <p>Try broadening your search or location filter.</p>
                    </div>
                )}
            </div>

            {selectedJob && (
                <PublicJobDetail
                    jobId={selectedJob._id}
                    onClose={() => setSelectedJob(null)}
                    onApply={() => handleApply(selectedJob)}
                    appStatus={applications[selectedJob._id]}
                />
            )}
        </div>
    );
};

export default StudentJobPortal;
